import React from 'react'
import { AlertTriangle } from 'lucide-react'
import SEO from '../components/SEO'
import './FaqScreen.css'

const SUPPORT_EMAIL = 'pcrassisttool@gmail.com'

// Reasons not to reach for a general-purpose chatbot. Deliberately rendered
// without approving check icons: each row is a limitation, and a green tick
// beside "Not HIPAA ready" read as a PCRAssist feature.
const VS_CHATGPT = [
  {
    term: '$20/mo for heavy use',
    note: 'Extensive usage typically requires a paid subscription.',
  },
  {
    term: 'Inconsistent answers',
    note: 'General-purpose responses can vary widely across attempts.',
  },
  {
    term: 'No template storage',
    note: 'We store your templates to streamline your workflow.',
  },
  {
    term: 'Not HIPAA ready',
    note: 'Our design and data handling are aligned with HIPAA considerations.',
  },
]

function FaqScreen() {
  return (
    <>
      <SEO
        title="FAQ - PCRAssist"
        description="Find answers to frequently asked questions about PCRAssist's AI-powered protocol generation, features, and usage."
        canonicalUrl="/faq"
      />

      <div className="fq">
        <div className="fq__wrap">
          <header className="fq__head">
            <h1 className="fq__title">Frequently asked questions</h1>
            <p className="fq__lede">
              Answers to common questions about how PCRAssist works, data privacy, and why it saves
              you time.
            </p>
          </header>

          <p className="fq__thesis">
            We help you write high&#8209;quality PCRs faster.
          </p>

          <section className="fq__qa" aria-labelledby="fq-how">
            <h2 className="fq__q" id="fq-how">
              How does it work?
            </h2>
            <div className="fq__a">
              <p>
                Narrate what occurred during a call and we generate a SOAP or CHART&#8209;formatted
                PCR for you. If you have templates, we seamlessly integrate them. We can also refine
                language for medical precision and provide feedback on missing details and
                inconsistencies. You choose between the original, templated, or concise report.
              </p>
              <p>
                The system adapts to your preferences. If you rearrange sentences into different
                sections, we remember and apply those choices to similar sentences in the future. We
                also learn your preferred abbreviations over time.
              </p>
              <p>
                While the first few reports may take a little longer, it quickly adapts to your
                style, saving significant time on subsequent PCRs.
              </p>
              <p>
                You can use the app without registering. For maximum time savings, register and add
                your templates.
              </p>
            </div>
          </section>

          <section className="fq__qa" aria-labelledby="fq-chatgpt">
            <h2 className="fq__q" id="fq-chatgpt">
              Why not just use ChatGPT?
            </h2>
            <div className="fq__a">
              <ul className="fq__compare">
                {VS_CHATGPT.map((item) => (
                  <li key={item.term}>
                    <b>{item.term}</b>
                    <span>{item.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="fq__qa" aria-labelledby="fq-trust">
            <h2 className="fq__q" id="fq-trust">
              Can I rely on what it writes?
            </h2>
            <div className="fq__a">
              <div className="fq__warn" role="note">
                <AlertTriangle size={18} aria-hidden="true" />
                <div className="fq__warn-body">
                  <span className="fq__warn-label">Disclaimer</span>
                  <p>
                    We use Machine Learning and Artificial Intelligence to help create these
                    reports. These systems can make mistakes!
                  </p>
                  <p>
                    <b>
                      Please read your final PCR very carefully, and make sure that all information
                      is correct.
                    </b>
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="fq__qa" aria-labelledby="fq-hipaa">
            <h2 className="fq__q" id="fq-hipaa">
              Is my patient data safe?
            </h2>
            <div className="fq__a">
              <p>
                We follow HIPAA guidelines. We do not store your PCRs on our systems. All
                communication between components is encrypted via TLS. When communicating with
                third&#8209;party systems, data is anonymized first (removing references to person,
                date, and place).
              </p>
            </div>
          </section>

          <footer className="fq__foot">
            <p>
              For any questions or support issues, email us at{' '}
              <a className="fq__link" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <span className="fq__signoff">The team at PCRAssist.com</span>
          </footer>
        </div>
      </div>
    </>
  )
}

export default FaqScreen
